import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  addComment,
  documentDetails,
  getExternalEmployeeList,
  registrationStatus,
  removeAssignedGridDetails,
  updateCandidateNameDetails,
  updateDocumentStatus,
} from '../services';
import {
  KYCDocumentDataType,
  OnboardingInitialState,
  OnboardingResponseType,
} from '../utils/OnboardingTypes';
import { API_STATUS } from '../utils/Constant';
import { isAPIFailed, isAPISuccess } from '../utils/Util';

const initialState: OnboardingInitialState = {
  onboardingData: {
    status: '',
    responseInformation: {
      responseCode: '',
      responseMessage: '',
    },
    result: [],
    pagination: {
      currentPage: 0,
      pageSize: 0,
      totalPages: 0,
      totalItems: 0,
      fromItem: 0,
      toItem: 0,
    },
  },
  updateKYCDocumentStatus: {
    status: '',
    errorMessage: null,
    successMessage: null,
  },
  kycDocumentData: {
    status: '',
    responseInformation: {
      responseCode: '',
      responseMessage: '',
    },
    result: {
      notes: [],
      document: {
        documentId: 0,
        documentName: '',
        documentUrl: '',
        documentUrlBack: null,
        documentType: '',
        documentStatusName: '',
        documentStatus: '',
        educationQualification: null,
        bankName: null,
        panNo: null,
        accountHolderName: null,
        accountNumber: null,
        ifscCode: null,
        aadharNo: null,
      },
    },
  },
};

export const externalEmployeeList = createAsyncThunk(
  'onboarding/fetchList',
  async (dataParam: any) => {
    const res = await getExternalEmployeeList(dataParam.data, dataParam.params);
    return res.data;
  }
);

export const updateRegistrationStatus = createAsyncThunk(
  'onboarding/updateRegistrationStatus',
  async (data: any) => {
    const res = await registrationStatus(data);
    return res.data;
  }
);
export const saveCommentNote = createAsyncThunk(
  'onboarding/saveComment',
  async (data: any) => {
    const res = await addComment(data);
    return res.data;
  }
);
export const updateCandidateName = createAsyncThunk(
  'onboarding/updateCandidateName',
  async (data: any) => {
    const res = await updateCandidateNameDetails(data);
    return res.data;
  }
);
export const removeAssignedGrid = createAsyncThunk(
  'onboarding/removeGrid',
  async (data: any) => {
    const res = await removeAssignedGridDetails(data);
    return res.data;
  }
);
export const updateKYCDocument = createAsyncThunk(
  'onboarding/updateKYCDocument',
  async (data: any) => {
    const res = await updateDocumentStatus(data);
    return res.data;
  }
);
/**
 * Async API call to find kyc document details
 */
export const getDocumentDetails = createAsyncThunk(
  'onboarding/getDocumentDetails',
  async (data: { candidateId: number; documentId: number }) => {
    const res = await documentDetails(data.candidateId, data.documentId);
    return res.data;
  }
);
const updateKYCDocumentFailed = (state: any, action: any) => {
  state.updateKYCDocumentStatus.status = API_STATUS.FAILED;
  state.updateKYCDocumentStatus.successMessage = null;
  state.updateKYCDocumentStatus.errorMessage =
    action?.payload?.responseInformation.responseMessage;
};
export const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    resetOnboardingData: (state) => {
      state.onboardingData.result = [];
    },
    resetUpdateKYCDocumentStatus: (state) => {
      state.updateKYCDocumentStatus.status = '';
      state.updateKYCDocumentStatus.errorMessage = '';
      state.updateKYCDocumentStatus.successMessage = '';
    },
    resetKYCDocumentDetailsData: (state) => {
      state.kycDocumentData.result = {
        notes: [],
        document: {
          documentId: 0,
          documentName: '',
          documentUrl: '',
          documentUrlBack: null,
          documentType: '',
          documentStatusName: '',
          documentStatus: '',
          educationQualification: null,
          bankName: null,
          panNo: null,
          accountHolderName: null,
          accountNumber: null,
          ifscCode: null,
          aadharNo: null,
        },
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(externalEmployeeList.pending, (state) => {
        state.onboardingData.status = API_STATUS.LOADING;
      })
      .addCase(
        externalEmployeeList.fulfilled,
        (state, action: PayloadAction<OnboardingResponseType>) => {
          state.onboardingData.status = API_STATUS.SUCCEEDED;
          state.onboardingData.pagination = action.payload.pagination;
          state.onboardingData.result = action.payload.result;
          state.onboardingData.responseInformation =
            action.payload.responseInformation;
        }
      )
      .addCase(externalEmployeeList.rejected, (state) => {
        state.onboardingData.status = API_STATUS.FAILED;
      })
      .addCase(getDocumentDetails.pending, (state) => {
        state.kycDocumentData.status = API_STATUS.LOADING;
      })
      .addCase(
        getDocumentDetails.fulfilled,
        (state, action: PayloadAction<KYCDocumentDataType>) => {
          state.kycDocumentData.status = API_STATUS.SUCCEEDED;
          state.kycDocumentData.result = action.payload.result;
          state.kycDocumentData.responseInformation =
            action.payload.responseInformation;
        }
      )
      .addCase(getDocumentDetails.rejected, (state) => {
        state.kycDocumentData.status = API_STATUS.FAILED;
      })
      .addCase(updateKYCDocument.pending, (state) => {
        state.updateKYCDocumentStatus.status = API_STATUS.LOADING;
      })
      .addCase(updateKYCDocument.fulfilled, (state, action) => {
        state.updateKYCDocumentStatus.status = API_STATUS.SUCCEEDED;
        if (isAPISuccess(action.payload)) {
          state.updateKYCDocumentStatus.successMessage =
            action.payload.responseInformation.responseMessage;
          state.updateKYCDocumentStatus.errorMessage = null;
        } else if (
          isAPIFailed(action.payload) &&
          action.payload.responseInformation.responseMessage.includes(
            'Rejected'
          )
        ) {
          updateKYCDocumentFailed(state, action);
        } else {
          updateKYCDocumentFailed(state, action);
        }
      })
      .addCase(updateKYCDocument.rejected, (state, action) => {
        updateKYCDocumentFailed(state, action);
      });
  },
});

export const registrationApprovalSelector = (state: {
  onboarding: OnboardingInitialState;
}) => state.onboarding.onboardingData;

export const updateKYCDocumentStatusSelector = (state: {
  onboarding: OnboardingInitialState;
}) => state.onboarding.updateKYCDocumentStatus;

export const kycDocumentDetailsSelector = (state: {
  onboarding: OnboardingInitialState;
}) => state.onboarding.kycDocumentData;

export const {
  resetOnboardingData,
  resetUpdateKYCDocumentStatus,
  resetKYCDocumentDetailsData,
} = onboardingSlice.actions;
export default onboardingSlice.reducer;
